import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Trophy, BarChart3, Calendar, Package, Clock, AlertTriangle, PhoneCall, X, ExternalLink, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  getProdutividadeEquipe, getEntregasPorCategoria, getProdutividadeDetalhe,
  MetricaDetalhe, ProdutividadePessoa,
} from "../services/relatorios";
import { Avatar } from "../components/Avatar";

/**
 * AJUDA da tela (pedido da Thainá, jul/2026: "não consegui entender 100% do que
 * se trata"). Explica em linguagem simples o que é cada número, por que o placar
 * é separado por equipe e como sai o Destaque do mês.
 */
function AjudaModal({ onFechar }: { onFechar: () => void }) {
  const bloco = "bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3";
  const titulo = "font-semibold text-slate-800 dark:text-slate-100 mb-1";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onFechar}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <HelpCircle size={18} className="text-blue-600" />
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex-1">Como ler esta tela</h3>
          <button onClick={onFechar} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Fechar">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4 text-sm text-slate-700 dark:text-slate-200">
          <p>
            Esta tela mostra <strong>o que cada pessoa produziu no período</strong> que você escolher lá em cima
            (as duas datas). Ela serve para acompanhar a equipe e para escolher o <strong>Destaque do mês</strong>.
          </p>

          <div className={bloco}>
            <p className={titulo}>Por que está separado por equipe?</p>
            <p>
              Porque cada função entrega uma coisa diferente. Quem edita vídeo passa horas dentro da tarefa;
              quem faz atendimento vive cobrando cliente, organizando pauta e resolvendo pepino — trabalho que
              quase não vira "tempo no card". Se medíssemos todo mundo pela mesma régua, o atendimento sempre
              pareceria improdutivo, o que seria injusto. Então <strong>cada equipe é comparada só com ela mesma</strong>.
            </p>
            <p className="mt-1.5 text-slate-500 dark:text-slate-400 text-[13px]">
              A equipe de cada pessoa é definida no cadastro dela (Usuários → Setor). Quem ainda não tem setor
              aparece agrupado em "Sem setor".
            </p>
          </div>

          <div className={bloco}>
            <p className={titulo}>O número grande e a barra verde</p>
            <p>
              É a <strong>quantidade de peças entregues</strong> no período — o número que mais importa no dia a dia.
              A barra é só visual: quem entregou mais tem a barra mais cheia.
            </p>
            <p className="mt-1.5">
              Ao lado, em pequeno, ficam os <strong>pontos</strong>: um resumo de tudo numa nota só, que leva em conta
              também prazo e retrabalho. É o que define o <strong>★ Destaque</strong> no alto de cada equipe — por isso
              nem sempre o Destaque é quem entregou mais peças: quem entrega bem-feito e no prazo pontua mais.
            </p>
            <p className="mt-1.5 text-slate-500 dark:text-slate-400 text-[13px]">
              No Atendimento a barra volta a ser por pontos, porque essa equipe não gera peças.
            </p>
          </div>

          <div className={bloco}>
            <p className={titulo}>O que conta como "entregue"?</p>
            <p>
              Somente os cards <strong>finalizados</strong> — ou seja, que chegaram na coluna Finalizado dentro do
              período escolhido. Card criado e ainda em andamento <em>não</em> conta aqui, mesmo já tendo trabalho feito.
            </p>
            <p className="mt-1.5">
              E conta <strong>peças, não cards</strong>: se um card foi cadastrado com 3 peças, ele soma 3.
              A peça é creditada ao <strong>responsável principal</strong> do card (quem está em primeiro lugar);
              quem entrou como apoio não recebe a contagem.
            </p>
          </div>

          <div className={bloco}>
            <p className={titulo}>Os números da equipe de produção</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong>Peças</strong> — quantas peças foram entregues (um card pode valer várias peças).</li>
              <li><strong>Tempo</strong> — horas trabalhadas nas tarefas, contando só o horário comercial.</li>
              <li><strong>% prazo</strong> — de tudo que a pessoa concluiu, quanto ficou dentro do prazo.</li>
              <li><strong>% retrab.</strong> — quanto do tempo dela foi gasto refazendo coisa (ajustes). Aqui <em>quanto menor, melhor</em>.</li>
            </ul>
            <p className="mt-2 text-slate-600 dark:text-slate-300">
              <strong>Como vira pontos:</strong> 10 por peça + 1 por hora, com um bônus de quem cumpre prazo e
              um desconto para quem tem muito retrabalho. Ou seja: não premia só quem faz muito, premia também
              quem faz <em>bem-feito e no prazo</em>.
            </p>
          </div>

          <div className={bloco}>
            <p className={titulo}>Os números da equipe de atendimento</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong>Cobranças</strong> — quantas vezes cobrou o cliente (cada clique em "cobrei o cliente" no card conta).</li>
              <li><strong>% data cli.</strong> — das datas combinadas com o cliente no período, quantas foram cumpridas.</li>
              <li><strong>Capt.</strong> — quantas captações foram agendadas.</li>
              <li><strong>+48h</strong> — cards parados há mais de 48 horas esperando o cliente <em>neste momento</em>. Aqui <em>quanto menor, melhor</em>.</li>
            </ul>
            <p className="mt-2 text-slate-600 dark:text-slate-300">
              <strong>Como vira pontos:</strong> o que mais vale é <em>cumprir a data combinada com o cliente</em>
              (o resultado), depois vêm as cobranças e as captações. Cards parados descontam — mas esse desconto
              tem um limite, para uma fila antiga não derrubar o mês inteiro.
            </p>
            <p className="mt-2 rounded-md bg-blue-50 dark:bg-blue-900/30 px-3 py-2 text-blue-800 dark:text-blue-200">
              💡 <strong>Dica:</strong> clique em qualquer um desses quatro números para ver <strong>exatamente
              quais cards</strong> estão por trás dele — e clicar num card abre a tarefa.
            </p>
          </div>

          <div className={bloco}>
            <p className={titulo}>Duas coisas importantes de saber</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>
                O número de <strong>+48h é uma foto de agora</strong>, não do período escolhido. Ele mostra a fila
                parada hoje, mesmo que você esteja olhando um mês passado.
              </li>
              <li>
                Os dados de <strong>cobrança começaram a ser guardados agora</strong>. Meses anteriores aparecem
                zerados nessa coluna — não é erro, é que o sistema ainda não registrava isso.
              </li>
            </ul>
          </div>

          <p className="text-slate-500 dark:text-slate-400 text-[13px]">
            Os pesos da pontuação podem ser ajustados: se a Rec decidir que uma coisa vale mais que outra, é só pedir.
          </p>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 text-right">
          <button onClick={onFechar} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

const ROTULO_METRICA: Record<MetricaDetalhe, string> = {
  cobrancas: "Cobranças registradas",
  data_cliente: "Datas combinadas com o cliente",
  captacoes: "Captações agendadas",
  estourados: "Cards parados há mais de 48h (agora)",
};

/**
 * "De onde saiu esse número" — abre a lista de cards por trás de uma métrica do
 * placar. Um placar que vira bonificação precisa ser auditável: sem isso, o
 * gestor só vê o número e não tem como responder "quais foram?".
 */
function DetalheMetricaModal({ pessoa, metrica, inicio, fim, onFechar }: {
  pessoa: ProdutividadePessoa; metrica: MetricaDetalhe; inicio: string; fim: string; onFechar: () => void;
}) {
  const { data: linhas = [], isLoading } = useQuery({
    queryKey: ["produtividade-detalhe", pessoa.usuario.id, metrica, inicio, fim],
    queryFn: () => getProdutividadeDetalhe({ usuarioId: pessoa.usuario.id, metrica, inicio, fim }),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onFechar}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
          <Avatar nome={pessoa.usuario.nome} fotoUrl={pessoa.usuario.fotoUrl} tamanho={28} />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">{pessoa.usuario.nome}</p>
            <p className="text-xs text-slate-500">{ROTULO_METRICA[metrica]} · {linhas.length} {linhas.length === 1 ? "item" : "itens"}</p>
          </div>
          <button onClick={onFechar} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto p-3">
          {isLoading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Carregando…</p>
          ) : linhas.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Nada no período.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {linhas.map((l, i) => (
                <li key={`${l.tarefaId}-${i}`}>
                  <Link
                    to={`/tarefas?tarefa=${l.tarefaId}`}
                    className="flex items-center gap-2 py-2 px-2 -mx-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 group text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-slate-700 dark:text-slate-200 group-hover:text-blue-600">{l.titulo}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {l.projetoNome || "Avulsa"}{l.clienteNome ? ` · ${l.clienteNome}` : ""}
                      </p>
                    </div>
                    {/* Cada métrica mostra o dado que importa nela */}
                    {metrica === "estourados" && (
                      <span className="text-[11px] text-red-500 font-medium shrink-0 tabular-nums">
                        {l.diasParado}d parado
                        {l.ultimaCobrancaEm ? "" : " · nunca cobrado"}
                      </span>
                    )}
                    {metrica === "data_cliente" && (
                      <span className={`text-[11px] shrink-0 font-medium ${l.ok ? "text-emerald-600" : "text-red-500"}`}>
                        {l.ok ? "cumpriu" : "não cumpriu"}
                        {l.data ? ` · ${new Date(l.data).toLocaleDateString("pt-BR")}` : ""}
                      </span>
                    )}
                    {(metrica === "cobrancas" || metrica === "captacoes") && l.data && (
                      <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">
                        {new Date(l.data).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                    <ExternalLink size={13} className="text-slate-300 group-hover:text-blue-600 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function primeiroDiaMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDuracao(ms: number) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function Produtividade() {
  const { can } = useAuth();
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeISO());
  // Qual número foi clicado (abre a lista de cards por trás dele).
  const [detalhe, setDetalhe] = useState<{ pessoa: ProdutividadePessoa; metrica: MetricaDetalhe } | null>(null);
  const [ajuda, setAjuda] = useState(false);

  const periodoValido = !!inicio && !!fim && inicio <= fim;

  const { data: equipe = [], isLoading: loadingEquipe, isError: errEquipe } = useQuery({
    queryKey: ["produtividade-equipe", inicio, fim],
    queryFn: () => getProdutividadeEquipe(inicio, fim),
    enabled: can(["gestor", "admin"]) && periodoValido,
    placeholderData: keepPreviousData,
  });
  const { data: categorias = [], isLoading: loadingCat, isError: errCat } = useQuery({
    queryKey: ["entregas-por-categoria", inicio, fim],
    queryFn: () => getEntregasPorCategoria(inicio, fim),
    enabled: can(["gestor", "admin"]) && periodoValido,
    placeholderData: keepPreviousData,
  });

  if (!can(["gestor", "admin"])) {
    return <div className="text-slate-500">Acesso restrito.</div>;
  }

  const maxCat = Math.max(1, ...categorias.map((c) => c.quantidade));
  const totalEntregas = equipe.reduce((s, e) => s + e.entregas, 0);

  // Agrupa por SETOR (pedido da Rec: editor e atendimento não competem na mesma
  // régua). "Sem setor" vai por último. Cada grupo tem seu "Destaque do mês".
  const gruposSetor = (() => {
    const mapa = new Map<string, typeof equipe>();
    for (const e of equipe) {
      const chave = e.setor?.trim() || "Sem setor";
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push(e);
    }
    return [...mapa.entries()]
      .map(([setor, pessoas]) => ({
        setor,
        pessoas: [...pessoas].sort((a, b) => b.pontos - a.pontos || b.entregas - a.entregas),
      }))
      .sort((a, b) => (a.setor === "Sem setor" ? 1 : b.setor === "Sem setor" ? -1 : a.setor.localeCompare(b.setor)));
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Trophy size={22} className="text-amber-500" /> Produtividade da equipe
            {/* Ajuda: a tela tem muito número e nada explicava (pedido da Thainá). */}
            <button
              type="button"
              onClick={() => setAjuda(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline border border-blue-200 dark:border-blue-800 rounded-full px-2.5 py-1"
              title="Entender o que significa cada número desta tela"
            >
              <HelpCircle size={14} /> Como ler esta tela
            </button>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            O que cada pessoa produziu no período, comparado dentro da própria equipe.{" "}
            <button type="button" onClick={() => setAjuda(true)} className="text-blue-600 hover:underline">
              Não entendeu algum número? Clique aqui.
            </button>
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-2">
          <Calendar size={16} className="text-slate-400" />
          <input type="date" value={inicio} max={fim} onChange={(e) => setInicio(e.target.value)} className="text-sm px-2 py-1 outline-none bg-transparent dark:text-slate-100" />
          <span className="text-slate-400">a</span>
          <input type="date" value={fim} min={inicio} onChange={(e) => setFim(e.target.value)} className="text-sm px-2 py-1 outline-none bg-transparent dark:text-slate-100" />
        </div>
      </div>

      {!periodoValido && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={15} /> A data inicial precisa ser anterior ou igual à final.
        </div>
      )}
      {(errEquipe || errCat) && periodoValido && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle size={15} /> Erro ao carregar o relatório. Tente novamente.
        </div>
      )}

      {/* Placar da equipe — POR SETOR, com Destaque do mês por equipe. */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
          <Package size={18} />
          <h3 className="font-semibold">Placar por equipe — Destaque do mês</h3>
          {totalEntregas > 0 && <span className="text-xs font-normal text-slate-400">({totalEntregas} peças no total)</span>}
          <span className="relative group/pts inline-flex ml-0.5">
            <span tabIndex={0} className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-500 text-slate-400 text-[10px] font-bold flex items-center justify-center cursor-help">i</span>
            <span className="pointer-events-none absolute left-0 top-full mt-1 w-64 rounded-md bg-slate-800 text-white text-[11px] leading-snug px-2.5 py-2 shadow-lg opacity-0 group-hover/pts:opacity-100 group-focus-within/pts:opacity-100 transition-opacity z-40">
              O número grande é <b>peças entregues</b> (cards finalizados no período). Os <b>pts</b> ao lado
              definem o ★ Destaque — cada equipe tem a <b>sua régua</b>.<br />
              <b>Produção</b>: 10 por peça + 1 por hora útil + bônus de prazo − penalidade de retrabalho.<br />
              <b>Atendimento</b>: peso no resultado — % das datas do cliente cumpridas + 2 por cobrança + 5 por captação + 2 por peça − 5 por card parado +48h (essa penalidade para em −30, porque a fila parada é acúmulo antigo e não pode afundar o mês).<br />
              O <b>Destaque</b> é o maior pontos de cada equipe. Os pesos são ajustáveis. <b>Clique nos números do atendimento</b> pra ver de onde vieram.
            </span>
          </span>
        </div>

        {loadingEquipe ? (
          <p className="text-slate-400 text-sm">Carregando…</p>
        ) : equipe.length === 0 ? (
          <p className="text-slate-400 text-sm">Sem dados no período.</p>
        ) : (
          gruposSetor.map((grupo) => {
            const destaque = grupo.pessoas[0];
            const maxPontos = Math.max(1, ...grupo.pessoas.map((p) => p.pontos));
            // A barra e o número grande são as ENTREGAS (pedido da Thainá, ago/2026:
            // "o mais importante é a quantidade de entregas, poderia ficar no lugar
            // do ponto"). Os pontos continuam existindo — pequenos — porque são eles
            // que definem o ★ Destaque do mês (prazo e retrabalho pesam ali).
            // Se a equipe inteira não gera peças (é o caso do Atendimento), a barra
            // volta a ser por pontos: uma fileira de barras vazias não diz nada.
            const maxEntregas = Math.max(...grupo.pessoas.map((p) => p.entregas));
            const barraPorEntregas = maxEntregas > 0;
            return (
              <div key={grupo.setor} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 bg-slate-50/60 dark:bg-slate-800/30">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{grupo.setor}</span>
                  {destaque && destaque.pontos > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 px-2.5 py-1 rounded-full">
                      <Trophy size={13} /> Destaque: {destaque.usuario.nome}
                    </span>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  {grupo.pessoas.map((e, idx) => (
                    <div key={e.usuario.id} className="flex items-center gap-3">
                      <span className={`w-5 text-center text-sm font-bold ${idx === 0 && e.pontos > 0 ? "text-amber-500" : "text-slate-400"}`}>
                        {idx === 0 && e.pontos > 0 ? "★" : idx + 1}
                      </span>
                      <Avatar nome={e.usuario.nome} fotoUrl={e.usuario.fotoUrl} tamanho={32} />
                      <div className="w-28 sm:w-40 truncate text-sm text-slate-700 dark:text-slate-200 flex items-center gap-1" title={e.usuario.nome}>
                        <span className="truncate">{e.usuario.nome}</span>
                        {e.ativo === false && <span className="text-[9px] uppercase font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded shrink-0">ex</span>}
                      </div>
                      <div
                        className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-4 overflow-hidden min-w-[60px]"
                        title={barraPorEntregas ? "Peças entregues no período" : "Pontuação (esta equipe não gera peças)"}
                      >
                        <div
                          className={`${barraPorEntregas ? "bg-emerald-500" : "bg-amber-500"} h-full rounded-full transition-all`}
                          style={{ width: `${(barraPorEntregas ? e.entregas / maxEntregas : e.pontos / maxPontos) * 100}%` }}
                        />
                      </div>
                      {/* ENTREGAS em destaque; pontos discretos, só pra explicar o ★. */}
                      <div className="w-20 text-right leading-tight" title="Peças entregues no período">
                        <span className="text-lg font-bold tabular-nums text-slate-800 dark:text-slate-100">{e.entregas}</span>
                        <span className="text-[10px] text-slate-400 ml-1">{e.entregas === 1 ? "peça" : "peças"}</span>
                      </div>
                      <div
                        className="w-12 text-right text-xs tabular-nums text-amber-600 dark:text-amber-400 shrink-0"
                        title="Pontuação do período — é ela que define o ★ Destaque do mês"
                      >
                        {e.pontos} pts
                      </div>
                      {/* Métricas de apoio — o ATENDIMENTO tem as próprias
                          (cobrar/prazo do cliente/captação), porque peças e horas
                          não representam o trabalho dele. */}
                      {e.ehAtendimento ? (
                        // Números CLICÁVEIS: abrem a lista de cards por trás deles.
                        <div className="hidden md:flex items-center gap-3 text-xs text-slate-500 tabular-nums">
                          <button type="button" onClick={() => setDetalhe({ pessoa: e, metrica: "cobrancas" })}
                            className="w-16 text-right flex items-center justify-end gap-1 hover:text-blue-600 hover:underline"
                            title="Cobranças ao cliente no período — clique para ver quais">
                            <PhoneCall size={11} /> {e.cobrancas}
                          </button>
                          <button type="button" onClick={() => setDetalhe({ pessoa: e, metrica: "data_cliente" })}
                            className="w-20 text-right hover:text-blue-600 hover:underline"
                            title={`Datas combinadas com o cliente cumpridas (${e.dataClienteTotal} no período) — clique para ver quais`}>
                            {e.dataClienteOkPct == null ? "—" : `${e.dataClienteOkPct}%`}<span className="text-[9px] text-slate-400"> data cli.</span>
                          </button>
                          <button type="button" onClick={() => setDetalhe({ pessoa: e, metrica: "captacoes" })}
                            className="w-14 text-right hover:text-blue-600 hover:underline"
                            title="Captações agendadas no período — clique para ver quais">
                            {e.captacoes}<span className="text-[9px] text-slate-400"> capt.</span>
                          </button>
                          <button type="button" onClick={() => setDetalhe({ pessoa: e, metrica: "estourados" })}
                            className={`w-16 text-right hover:underline ${e.cardsEstourados > 0 ? "text-red-500 hover:text-red-600" : "hover:text-blue-600"}`}
                            title="Cards parados há mais de 48h AGORA (foto do momento) — clique para ver quais">
                            {e.cardsEstourados}<span className="text-[9px] text-slate-400"> +48h</span>
                          </button>
                        </div>
                      ) : (
                        <div className="hidden md:flex items-center gap-3 text-xs text-slate-500 tabular-nums">
                          {/* As peças saíram daqui — viraram o número grande da linha. */}
                          <span className="w-20 text-right flex items-center justify-end gap-1" title="Tempo útil"><Clock size={11} /> {fmtDuracao(e.tempoUtilMs)}</span>
                          <span className="w-14 text-right" title="% concluídas no prazo">{e.noPrazoPct == null ? "—" : `${e.noPrazoPct}%`}<span className="text-[9px] text-slate-400"> prazo</span></span>
                          <span className={`w-16 text-right ${e.retrabalhoPct >= 30 ? "text-red-500" : ""}`} title="% do tempo em ajustes (retrabalho)">{e.retrabalhoPct}%<span className="text-[9px] text-slate-400"> retrab.</span></span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Entregas por categoria */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-100">
          <BarChart3 size={18} /> Peças entregues por tipo
        </h3>
        {loadingCat ? (
          <p className="text-slate-400 text-sm">Carregando…</p>
        ) : categorias.length === 0 ? (
          <p className="text-slate-400 text-sm">Sem dados no período.</p>
        ) : (
          <div className="space-y-3">
            {categorias.map((c) => (
              <div key={c.tipoTarefaId ?? "sem"} className="flex items-center gap-4">
                <div className="w-40 text-sm truncate text-slate-700 dark:text-slate-200 flex items-center gap-2" title={c.label}>
                  <span className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: c.cor || "#94a3b8" }} />
                  {c.label}
                </div>
                <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-4 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(c.quantidade / maxCat) * 100}%`, backgroundColor: c.cor || "#94a3b8" }} />
                </div>
                <div className="w-12 text-right text-sm font-medium tabular-nums text-slate-700 dark:text-slate-200">{c.quantidade}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {ajuda && <AjudaModal onFechar={() => setAjuda(false)} />}

      {detalhe && (
        <DetalheMetricaModal
          pessoa={detalhe.pessoa}
          metrica={detalhe.metrica}
          inicio={inicio}
          fim={fim}
          onFechar={() => setDetalhe(null)}
        />
      )}
    </div>
  );
}
